import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    CheckCircle2, Circle, ArrowRight, SkipForward, RotateCcw, Sparkles,
} from "lucide-react";

/**
 * /onboarding — "Build Your VAULT" activation checklist.
 *
 * Six live-computed steps + progress ring. Skip anything, come back
 * anytime. Once everything is resolved the page nudges the user
 * gently to the dashboard — never blocks navigation.
 */
function Ring({ percent }) {
    const R = 38, C = 2 * Math.PI * R;
    const off = C - (Math.min(100, Math.max(0, percent)) / 100) * C;
    return (
        <svg width="96" height="96" viewBox="0 0 96 96" className="shrink-0">
            <circle cx="48" cy="48" r={R} fill="none"
                stroke="rgba(11,30,63,0.08)" strokeWidth="6" />
            <circle cx="48" cy="48" r={R} fill="none"
                stroke="#c9a253" strokeWidth="6" strokeLinecap="round"
                strokeDasharray={C} strokeDashoffset={off}
                transform="rotate(-90 48 48)"
                style={{ transition: "stroke-dashoffset 600ms ease" }} />
            <text x="48" y="52" textAnchor="middle"
                fontFamily="Fraunces, serif" fontSize="22" fill="#0b1e3f">
                {percent}%
            </text>
        </svg>
    );
}

function StepRow({ step, onSkip, onUnskip, onGo }) {
    const done = step.done;
    const skipped = step.skipped;
    const Icon = done ? CheckCircle2 : Circle;
    const iconCls = done
        ? "text-emerald-600"
        : skipped ? "text-stone-400" : "text-stone-400";
    return (
        <Card data-testid={`onb-step-${step.key}`}
              className={`transition ${done ? "bg-emerald-50/40" : skipped ? "bg-stone-50 opacity-70" : "bg-white"}`}>
            <CardContent className="pt-4 pb-4 flex items-start gap-3">
                <Icon size={22} className={`${iconCls} mt-0.5`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="font-['Fraunces',serif] text-lg text-[#0b1e3f]">
                            {step.title}
                        </div>
                        {done && (
                            <span data-testid={`onb-badge-done-${step.key}`}
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 uppercase tracking-widest font-semibold">
                                Done
                            </span>
                        )}
                        {skipped && (
                            <span data-testid={`onb-badge-skipped-${step.key}`}
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-200 text-stone-600 uppercase tracking-widest font-semibold">
                                Skipped
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-stone-600 mt-0.5">{step.why}</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {!done && (
                            <Button data-testid={`onb-cta-${step.key}`} size="sm"
                                onClick={() => onGo(step.cta.href)}
                                className="bg-[#0b1e3f] text-[#f6f3ec] hover:bg-[#0b1e3f]/90">
                                {step.cta.label} <ArrowRight size={14} className="ml-1" />
                            </Button>
                        )}
                        {!done && !skipped && (
                            <Button data-testid={`onb-skip-${step.key}`} size="sm" variant="ghost"
                                onClick={() => onSkip(step.key)}>
                                <SkipForward size={14} className="mr-1" />Skip for now
                            </Button>
                        )}
                        {skipped && (
                            <Button data-testid={`onb-unskip-${step.key}`} size="sm" variant="outline"
                                onClick={() => onUnskip(step.key)}>
                                <RotateCcw size={14} className="mr-1" />Bring back
                            </Button>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function Onboarding() {
    const [state, setState] = useState(null);
    const [busy, setBusy]   = useState(false);
    const nav = useNavigate();

    const load = async () => {
        try {
            const r = await api.get("/api/onboarding/state");
            setState(r.data);
        } catch (_e) {
            toast.error("Couldn't load onboarding state");
        }
    };

    useEffect(() => { load(); }, []);

    const skip = async (key) => {
        setBusy(true);
        try {
            await api.post(`/api/onboarding/skip/${key}`);
            toast.success("You can come back to this anytime");
            load();
        } finally { setBusy(false); }
    };
    const unskip = async (key) => {
        setBusy(true);
        try {
            await api.post(`/api/onboarding/unskip/${key}`);
            load();
        } finally { setBusy(false); }
    };
    const go = (href) => nav(href);

    if (!state) {
        return (
            <div className="min-h-screen bg-[#f6f3ec] flex items-center justify-center text-stone-500">
                Loading your VAULT…
            </div>
        );
    }

    return (
        <div data-testid="onboarding-page" className="min-h-screen bg-[#f6f3ec] text-[#0b1e3f]">
            <div className="max-w-3xl mx-auto p-6">
                <div className="flex items-center gap-6 mb-6">
                    <Ring percent={state.percent_resolved} />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10.5px] uppercase tracking-widest text-[#a0894a]">
                            Build your VAULT
                        </div>
                        <h1 className="font-['Fraunces',serif] text-3xl leading-tight mt-1">
                            {state.all_resolved
                                ? "Your VAULT is set up. Beautifully."
                                : "A calm start. Six small moves."}
                        </h1>
                        <p className="text-sm text-stone-600 mt-2">
                            {state.all_resolved
                                ? "Everything essential is in place. You can come back to any step later."
                                : "Do them in any order. Skip anything. VAULT keeps track quietly in the background."}
                        </p>
                        <div className="text-xs text-stone-500 mt-2">
                            {state.done} of {state.total} complete · {state.resolved} of {state.total} resolved
                        </div>
                    </div>
                    {state.all_resolved && (
                        <Button data-testid="onb-open-dashboard"
                            onClick={() => nav("/dashboard")}
                            className="bg-[#0b1e3f] text-[#f6f3ec] hover:bg-[#0b1e3f]/90">
                            <Sparkles size={14} className="mr-1" />Open dashboard
                        </Button>
                    )}
                </div>

                <div className="space-y-3">
                    {state.steps.map(s => (
                        <StepRow key={s.key} step={s}
                            onSkip={skip} onUnskip={unskip} onGo={go} />
                    ))}
                </div>

                <div className="mt-8 text-center">
                    <Link to="/dashboard" data-testid="onb-skip-all"
                        className="text-xs text-stone-500 hover:text-stone-800 underline underline-offset-2">
                        I&apos;ll do this later — take me to the dashboard
                    </Link>
                </div>
            </div>
        </div>
    );
}
